import React from 'react';
import PropTypes from 'prop-types';
import { CheckIcon } from '../../assets/icons';

const STEP_ID_ALIASES = {
  KYC: 'PerformKYC',
  PerformKyc: 'PerformKYC',
  ApproveTermsAndConditions: 'AcceptLegalAgreements',
  TermsAndConditions: 'AcceptLegalAgreements',
};

const BASE_PHASES = [
  { id: 'VerifyPhoneNumber', label: 'Phone Number', stepIds: ['VerifyPhoneNumber'] },
  { id: 'ChooseUserType', label: 'Type', stepIds: ['ChooseUserType'] },
  { id: 'CollectData', label: 'About you', stepIds: ['CollectData'] },
  { id: 'Financial', label: 'Financial', stepIds: ['Financial'] },
  { id: 'Compliance', label: 'Compliance', stepIds: ['Compliance'] },
  { id: 'AcceptLegalAgreements', label: 'Terms', stepIds: ['AcceptLegalAgreements'] },
  { id: 'PerformKYC', label: 'Verify', stepIds: ['PerformKYC'] },
  { id: 'Onboarded', label: 'Review', stepIds: ['Onboarded'] },
];

/** Company top stepper follows HansePay design: Account → Company → Documents → Management & Owners → Activity → Terms → Verify */
const COMPANY_PHASES = [
  { id: 'Account', label: 'Account', stepIds: ['Account', 'VerifyPhoneNumber', 'ChooseUserType'] },
  { id: 'CollectData', label: 'Company', stepIds: ['CollectData'] },
  { id: 'CompanyDocuments', label: 'Documents', stepIds: ['CompanyDocuments'] },
  {
    id: 'CollectCompanyPersonData',
    label: 'Management & Owners',
    stepIds: ['CollectCompanyPersonData', 'CollectCompanyOwnersData'],
  },
  { id: 'Financial', label: 'Activity', stepIds: ['Financial', 'Compliance'] },
  { id: 'AcceptLegalAgreements', label: 'Terms', stepIds: ['AcceptLegalAgreements'] },
  { id: 'PerformKYC', label: 'Verify', stepIds: ['PerformKYC'] },
];

function getCanonicalStep(stepName) {
  if (!stepName) return null;
  return STEP_ID_ALIASES[stepName] ?? stepName;
}

function getStepName(step) {
  return typeof step === 'string' ? step : step?.step;
}

function getStepperPhases(userType, currentStep, stepsFromApi) {
  const canonicalCurrent = getCanonicalStep(currentStep);
  const hasCompanyPersonStep = Array.isArray(stepsFromApi)
    ? stepsFromApi.some((step) => getCanonicalStep(getStepName(step)) === 'CollectCompanyPersonData')
    : false;
  const shouldIncludeCompanySteps =
    userType === 'Company' ||
    canonicalCurrent === 'CollectCompanyPersonData' ||
    canonicalCurrent === 'CompanyDocuments' ||
    hasCompanyPersonStep;

  if (!shouldIncludeCompanySteps) return BASE_PHASES;

  return COMPANY_PHASES;
}

function findPhaseIndex(phases, currentStep) {
  const canonical = getCanonicalStep(currentStep);
  if (!canonical) return 0;
  const index = phases.findIndex((phase) => (phase.stepIds || [phase.id]).includes(canonical));
  return Math.max(0, index);
}

function getCompletedSteps(stepsFromApi) {
  const completed = new Set();
  if (!Array.isArray(stepsFromApi)) return completed;

  stepsFromApi.forEach((step) => {
    const stepName = getCanonicalStep(getStepName(step));
    if (stepName && typeof step === 'object' && step?.isCompleted === true) {
      completed.add(stepName);
    }
  });

  return completed;
}

function isPhaseDone(phase, index, currentIndex, completedSteps, useDisplayStepAsSourceOfTruth) {
  if (useDisplayStepAsSourceOfTruth) {
    return index < currentIndex;
  }
  if (index < currentIndex) return true;
  const ids = phase.stepIds || [phase.id];
  return ids.some((id) => completedSteps.has(id));
}

function OnboardingStepper({ onboardingStatus, displayCurrentStep, userType }) {
  const stepsFromApi = onboardingStatus?.steps;
  const currentStep = getCanonicalStep(displayCurrentStep ?? onboardingStatus?.currentStep);
  const useDisplayStepAsSourceOfTruth = typeof displayCurrentStep === 'string' && displayCurrentStep.length > 0;
  const phases = getStepperPhases(userType, currentStep, stepsFromApi);
  const currentIndex = findPhaseIndex(phases, currentStep);
  const completedSteps = getCompletedSteps(stepsFromApi);
  const currentPhase = phases[currentIndex] ?? phases[0];
  const progressPercent = phases.length > 1 ? (currentIndex / (phases.length - 1)) * 100 : 0;

  return (
    <div className='sticky top-0 z-[60] border-b border-[#0B1929]/[0.07] bg-white px-5 sm:px-10'>
      <div className='mx-auto max-w-5xl py-4 sm:hidden'>
        <div className='mb-2 flex items-center justify-between gap-3 text-[11.5px]'>
          <span className='min-w-0 truncate font-bold text-[#0B1929]'>{currentPhase?.label}</span>
          <span className='shrink-0 font-medium text-[#7A9AB0]'>
            Step {currentIndex + 1} of {phases.length}
          </span>
        </div>
        <div className='h-1.5 overflow-hidden rounded-full bg-[#DDE6EF]'>
          <div className='h-full rounded-full bg-[#1E4E80] transition-all' style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      <div className='mx-auto hidden max-w-5xl items-start py-5 sm:flex'>
        {phases.map((phase, index) => {
          const isDone = isPhaseDone(phase, index, currentIndex, completedSteps, useDisplayStepAsSourceOfTruth);
          const isCurrent = (phase.stepIds || [phase.id]).includes(currentStep) || index === currentIndex;
          let connectorClass = 'before:bg-[#DDE6EF]';
          let dotClass = 'border-[#DDE6EF] bg-white text-[#B0C4D8]';
          let labelClass = 'font-medium text-[#B0C4D8]';

          if (isDone && !isCurrent) {
            connectorClass = 'before:bg-[#1E4E80]';
            dotClass = 'border-[#1E4E80] bg-[#1E4E80] text-white';
            labelClass = 'font-medium text-[#7A9AB0]';
          } else if (isCurrent) {
            connectorClass = 'before:bg-gradient-to-r before:from-[#1E4E80] before:to-[#DDE6EF]';
            dotClass = 'border-[#0B1929] bg-[#0B1929] text-white shadow-[0_0_0_4px_rgba(11,25,41,0.1)]';
            labelClass = 'font-bold text-[#0B1929]';
          }

          return (
            <div
              key={phase.id}
              className={`relative flex flex-1 flex-col items-center ${
                index === 0
                  ? ''
                  : `before:absolute before:left-[calc(-50%+14px)] before:right-[calc(50%+14px)] before:top-[13px] before:z-0 before:h-0.5 ${connectorClass}`
              }`}
            >
              <div className={`relative z-[1] flex h-7 w-7 items-center justify-center rounded-full border-2 text-[11px] font-bold transition ${dotClass}`}>
                {isDone && !isCurrent ? <CheckIcon className='h-3.5 w-3.5' /> : index + 1}
              </div>
              <div className={`mt-2 whitespace-nowrap text-center text-[11.5px] transition ${labelClass}`}>{phase.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

OnboardingStepper.propTypes = {
  onboardingStatus: PropTypes.shape({
    currentStep: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ step: PropTypes.string, isCompleted: PropTypes.bool })])),
  }),
  displayCurrentStep: PropTypes.string,
  userType: PropTypes.string,
};

export default OnboardingStepper;
